import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { FreshnessBannerState } from '@components/layout/FreshnessBanner';

/**
 * Freshness context - lets the queue pages (rendered in the AppShell <Outlet/>) drive the shared
 * "Queue live as of HH:MM" banner in the topbar region. Pages call set() after each load so the
 * banner reflects the live multi-operator refresh state (fresh / stale-refreshing / blocked).
 */
interface FreshnessValue {
  state: FreshnessBannerState;
  label: string;
  set: (state: FreshnessBannerState, label: string) => void;
}

const Ctx = createContext<FreshnessValue | null>(null);

export function FreshnessProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<FreshnessBannerState>('fresh');
  const [label, setLabel] = useState('Queue loading...');
  const set = useCallback((s: FreshnessBannerState, l: string) => {
    setState(s);
    setLabel(l);
  }, []);
  const value = useMemo<FreshnessValue>(() => ({ state, label, set }), [state, label, set]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useFreshness(): FreshnessValue {
  const v = useContext(Ctx);
  // No-op fallback keeps pages usable outside a provider (e.g. in isolation tests).
  return v ?? { state: 'fresh', label: '', set: () => {} };
}

/** "Queue live as of 3:42 PM" style stamp. */
export function liveStamp(prefix = 'Queue live as of'): string {
  const now = new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${prefix} ${now}`;
}
